import type { AthenaPrincipal } from "../../runtime/data/principal.ts";
import { AthenaBillingCapabilityError } from "../errors.ts";
import type { BillingProviderConfigMap } from "../providers/types.ts";
import { executeLocalBillingCustomerCreate } from "../runtime/local/execute/customers.ts";
import type { BillingProviderRegistry } from "../runtime/local/providers/registry.ts";
import { PROCESS_BILLING_INVOCATION } from "../runtime/invocation-authority.ts";
import { selfDelegatedBillingPrincipal } from "../runtime/self/delegated-principal.ts";
import type { BillingCustomer } from "../types.ts";
import { requireBillingPrincipalUserId } from "./principal-user.ts";
import {
	createPostgresBillingSubjectRepository,
	isBillingUniqueViolation,
	subjectFromPrincipalUser,
} from "./postgres.ts";
import type {
	BillingSqlExecutor,
	BillingSubjectBindingRecord,
} from "./repository.ts";
import {
	requireReadyBillingConnectionId,
	resolveActiveBillingConnection,
} from "./sole-connection.ts";

function isPlaceholderProviderId(value: string): boolean {
	return value.startsWith("reserve:");
}

function usableProviderCustomer(
	bindings: readonly BillingSubjectBindingRecord[],
): BillingSubjectBindingRecord | undefined {
	return bindings.find(
		(binding) =>
			binding.providerSubjectKind === "customer" &&
			binding.status === "active" &&
			!isPlaceholderProviderId(binding.providerSubjectId),
	);
}

/**
 * Ensure an active provider customer exists for the Athena subject.
 * Binding keys are subject_kind + subject_id. Email is never identity.
 * Concurrent callers serialize on the live unique index: only the INSERT winner
 * creates a Mollie customer.
 */
export async function ensureActiveProviderCustomer(input: {
	configuredProviders?: BillingProviderConfigMap;
	idempotencyKey: string;
	principal: AthenaPrincipal;
	registry: BillingProviderRegistry;
	sql: BillingSqlExecutor;
	testMode?: boolean;
}): Promise<{ customerId: string; provider: BillingCustomer["provider"] }> {
	const connectionId = requireReadyBillingConnectionId(
		await resolveActiveBillingConnection(input.sql),
		"customers.create",
	);
	const createCustomer = () =>
		executeLocalBillingCustomerCreate({
			authority: PROCESS_BILLING_INVOCATION,
			configuredProviders: input.configuredProviders,
			payload: {
				idempotencyKey: `${input.idempotencyKey}:customer`,
				metadata: {
					athenaSubjectId: requireBillingPrincipalUserId(input.principal),
					athenaSubjectKind: "user",
				},
			},
			principal: selfDelegatedBillingPrincipal(input.principal, [
				"customers.create",
			]),
			registry: input.registry,
			testMode: input.testMode,
		});
	const repository = createPostgresBillingSubjectRepository(input.sql);
	const subject = subjectFromPrincipalUser(
		requireBillingPrincipalUserId(input.principal),
	);
	for (let attempt = 0; attempt < 8; attempt++) {
		const bindings = await repository.listActiveBindings({
			connectionId,
			subject,
		});
		const usable = usableProviderCustomer(bindings);
		if (usable) {
			return {
				customerId: usable.providerSubjectId,
				provider: "mollie",
			};
		}
		const reserved = await repository.reserve({
			connectionId,
			providerSubjectKind: "customer",
			source: "created",
			subject,
		});
		if (!reserved.inserted) {
			const existing =
				usableProviderCustomer([reserved.binding]) ??
				usableProviderCustomer(
					await repository.listActiveBindings({
						connectionId,
						subject,
					}),
				);
			if (existing) {
				return {
					customerId: existing.providerSubjectId,
					provider: "mollie",
				};
			}
			continue;
		}
		const created = await createCustomer();
		try {
			await repository.activate({
				id: reserved.binding.id,
				providerSubjectId: created.providerCustomerId,
			});
		} catch (error) {
			if (!isBillingUniqueViolation(error)) {
				throw error;
			}
			const after = usableProviderCustomer(
				await repository.listActiveBindings({
					connectionId,
					subject,
				}),
			);
			if (after) {
				return {
					customerId: after.providerSubjectId,
					provider: "mollie",
				};
			}
			throw error;
		}
		return {
			customerId: created.providerCustomerId,
			provider: created.provider,
		};
	}
	throw new AthenaBillingCapabilityError({
		operation: "customers.create",
		reason: "runtime_unavailable",
	});
}
